import {useCallback, useEffect, useMemo, useRef, type SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import useShow from '../../../hooks/useShow';
import useRestartXHR from '../../../hooks/useRestartXHR';
import setGlobalState, {resetGlobalState} from '../../../redux/setGlobalState';
import type {TState} from '../../../redux/store.type';
import {createAiAgentSubmitHandler} from '../utils/aiAgentSubmit';
import {useAiAgentAsk} from './useAiAgentAsk';
import {useAiAgentClearingFeeGate} from './useAiAgentClearingFeeGate';

export function useAiAgentPanel() {
    const show = useShow();
    const {t} = useTranslation('aiAgent');
    const navigate = useNavigate();
    const isOpen = useSelector((s: TState) => s.aiAgent?.isOpen);
    const sendMessageXHR = useSelector((s: TState) => s.aiAgent?.sendMessageXHR);
    const {
        ensureClearingFeeAcknowledged,
        resetClearingFeeAck,
        clearingFeePop,
    } = useAiAgentClearingFeeGate();

    const submitHandler = useMemo(
        () => createAiAgentSubmitHandler(navigate, {
            imageTooLarge: t('imageTooLarge'),
            imageUnsupported: t('imageUnsupported'),
            actionFailed: t('actionFailed'),
            clearingFeeRequired: t('clearingFeeRequired'),
            ensureClearingFeeAcknowledged,
        }),
        [navigate, t, ensureClearingFeeAcknowledged],
    );
    const {pendingAsk, clearAsk, handleSubmit} = useAiAgentAsk(submitHandler);
    useRestartXHR(sendMessageXHR, 'aiAgent.sendMessageXHR');

    // useShow returns a fresh object every render, so `show` itself must stay out of the
    // dependency list — a ref keeps the effect honest without re-running it each render.
    const showRef = useRef(show);
    showRef.current = show;
    const isPanelOpen = show.isOpen;

    useEffect(() => {
        if(isOpen && !isPanelOpen) {
            showRef.current.open();
        }
    }, [isOpen, isPanelOpen]);

    const openAgent = useCallback(() => {
        setGlobalState('aiAgent.isOpen', true);
        show.open();
    }, [show]);

    const closeAgent = useCallback(async (e?: Event | SyntheticEvent) => {
        setGlobalState('aiAgent.isOpen', false);
        // A request whose target page never mounted must not fire on a later visit.
        setGlobalState('aiAgent.openTicketPopupRequestId', 0);
        await show.close(e);
    }, [show]);

    const handleReset = useCallback(() => {
        clearAsk();
        resetClearingFeeAck();
        setGlobalState('aiAgent.sessionEventType', null);
        setGlobalState('aiAgent.sessionIntent', null);
        setGlobalState('aiAgent.chatLang', null);
        setGlobalState('aiAgent.activeFormId', null);
        setGlobalState('aiAgent.openTicketPopupRequestId', 0);
        setGlobalState('aiAgent.currentFormValues', null);
        resetGlobalState('aiAgent.pendingFormFill');
    }, [clearAsk, resetClearingFeeAck]);

    const agentShow = useMemo(() => ({
        ...show,
        open: openAgent,
        close: closeAgent,
    }), [show, openAgent, closeAgent]);

    return {
        t,
        agentShow,
        openAgent,
        handleReset,
        handleSubmit,
        pendingAsk,
        clearAsk,
        sendMessageXHR,
        clearingFeePop,
    };
}
